"use client";

import {
  Search,
  X as CloseIcon,
  UserPlus,
  Edit,
  Trash2,
  UserCheck,
  Clock,
  UserX,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "../../../../shadcn/ui/button";
import { Badge } from "../../../../shadcn/ui/badge";
import { FeatureAccess } from "../../../../utils/featureAccessControl";
import * as localService from "../../../../services/localService";

// User Row Component
function UserRow({ user, onEditUserDetails, onDeleteUser, canEditUsers, canDeleteUsers }) {
  const formatLastLogin = dateString => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatRelativeTime = dateString => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = status => {
    switch (status) {
      case "ACTIVE":
        return "bg-green-100 text-green-800";
      case "INACTIVE":
        return "bg-yellow-100 text-yellow-800";
      case "DISABLED":
        return "bg-red-100 text-red-800";
      case "SUSPENDED":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-4 px-3 sm:px-6 font-medium text-gray-900">
        <div className="flex items-center min-w-0">
          {user.imageUrl ? (
            <img
              src={user.imageUrl}
              alt={`${user.firstName} ${user.lastName}`}
              className="object-cover mr-2 sm:mr-3 w-8 h-8 rounded-full border"
            />
          ) : (
            <div className="mr-2 sm:mr-3 w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-medium">
              {(user.firstName?.[0] || "").toUpperCase()}
              {(user.lastName?.[0] || "").toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-gray-900 text-sm sm:text-base">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-xs text-gray-500 truncate">@{user.username}</div>
            <div className="text-xs text-gray-500 sm:hidden truncate">{user.emailAddress}</div>
          </div>
        </div>
      </td>
      <td className="py-4 px-3 sm:px-6 text-gray-600 hidden sm:table-cell">
        <div>
          <div className="text-sm font-medium">{user.emailAddress}</div>
          {user.msisdn && user.msisdn.trim() && <div className="text-xs text-gray-500">{user.msisdn}</div>}
        </div>
      </td>
      <td className="py-4 px-3 sm:px-6 text-gray-600 hidden md:table-cell">
        <div>
          <div className="text-sm font-medium">{user.organizationRole?.name || "N/A"}</div>
          <div className="text-xs text-gray-500">{user.organization?.name || "N/A"}</div>
        </div>
      </td>
      <td className="py-4 px-3 sm:px-6">
        <div className="flex flex-col gap-1">
          <Badge className={`text-xs ${getStatusColor(user.status)}`}>{user.status}</Badge>
          {user.requirePasswordChange && (
            <Badge className="text-xs bg-blue-100 text-blue-800">Account confirmation Required</Badge>
          )}
          {user.isOwner && <Badge className="text-xs bg-purple-100 text-purple-800">Owner</Badge>}
        </div>
      </td>
      <td className="py-4 px-3 sm:px-6 text-gray-600 hidden lg:table-cell">
        <div>
          <div className="text-sm font-medium">{formatLastLogin(user.lastLoggedInAt)}</div>
          <div className="text-xs text-gray-500">Created {formatRelativeTime(user.createdAt)}</div>
        </div>
      </td>
      <td className="py-4 px-3 sm:px-6">
        <div className="flex items-center gap-1 sm:gap-2">
          {canEditUsers && (
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEditUserDetails(user)}>
              <Edit className="h-4 w-4" />
            </Button>
          )}
          {canDeleteUsers && !user.isOwner && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDeleteUser(user)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Shimmer Loading Component
function TableShimmer() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 animate-pulse">
      <div className="p-6">
        {/* Table Header Shimmer */}
        <div className="flex space-x-6 mb-4">
          <div className="h-4 bg-gray-200 rounded w-16"></div>
          <div className="h-4 bg-gray-200 rounded w-20"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
          <div className="h-4 bg-gray-200 rounded w-20"></div>
          <div className="h-4 bg-gray-200 rounded w-16"></div>
          <div className="h-4 bg-gray-200 rounded w-16"></div>
        </div>

        {/* Table Rows Shimmer */}
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center space-x-6 py-4 border-b border-gray-100 last:border-b-0">
            {/* User column */}
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="space-y-1">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            </div>

            {/* Contact column */}
            <div className="space-y-1">
              <div className="h-4 bg-gray-200 rounded w-32"></div>
              <div className="h-3 bg-gray-200 rounded w-20"></div>
            </div>

            {/* Role column */}
            <div className="space-y-1">
              <div className="h-4 bg-gray-200 rounded w-28"></div>
              <div className="h-3 bg-gray-200 rounded w-20"></div>
            </div>

            {/* Status column */}
            <div className="space-y-1">
              <div className="h-6 bg-gray-200 rounded-full w-16"></div>
              <div className="h-4 bg-gray-200 rounded w-20"></div>
            </div>

            {/* Activity column */}
            <div className="space-y-1">
              <div className="h-4 bg-gray-200 rounded w-20"></div>
              <div className="h-3 bg-gray-200 rounded w-16"></div>
            </div>

            {/* Actions column */}
            <div className="flex space-x-2">
              <div className="w-8 h-8 bg-gray-200 rounded"></div>
              <div className="w-8 h-8 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ searchTerm }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
      <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <UserX className="w-12 h-12 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{searchTerm ? "No users found" : "No users yet"}</h3>
      <p className="text-gray-500 mb-4">
        {searchTerm
          ? `No users match "${searchTerm}". Try adjusting your search.`
          : "Get started by adding your first user to this role."}
      </p>
    </div>
  );
}

// User Table Component
function UserTable({ users, onEditUserDetails, onDeleteUser, searchTerm, canEditUsers, canDeleteUsers }) {
  if (!users || users.length === 0) {
    return <EmptyState searchTerm={searchTerm} />;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <table className="w-full min-w-[600px]">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-3 sm:px-6 font-medium text-gray-900 text-sm">User</th>
            <th className="text-left py-3 px-3 sm:px-6 font-medium text-gray-900 text-sm hidden sm:table-cell">
              Contact
            </th>
            <th className="text-left py-3 px-3 sm:px-6 font-medium text-gray-900 text-sm hidden md:table-cell">
              Role & Organization
            </th>
            <th className="text-left py-3 px-3 sm:px-6 font-medium text-gray-900 text-sm">Status & Type</th>
            <th className="text-left py-3 px-3 sm:px-6 font-medium text-gray-900 text-sm hidden lg:table-cell">
              Activity
            </th>
            <th className="text-left py-3 px-3 sm:px-6 font-medium text-gray-900 text-sm">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <UserRow
              key={index}
              user={user}
              onEditUserDetails={onEditUserDetails}
              onDeleteUser={onDeleteUser}
              canEditUsers={canEditUsers}
              canDeleteUsers={canDeleteUsers}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Search Header Component
function SearchHeader({ searchTerm, onSearchChange, onAddUser, shouldHideAddUser = false }) {
  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between mb-6 gap-3">
      <div className="relative flex-1 max-w-md">
        <input
          type="text"
          placeholder="Search users"
          value={searchTerm}
          onChange={e => onSearchChange(e.target.value)}
          className="w-full rounded-md bg-gray-50 pl-3 pr-10 border border-gray-200 py-2 focus:bg-white focus:border-gray-300 focus:outline-none text-sm"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
          {searchTerm ? (
            <CloseIcon
              className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-700"
              onClick={() => onSearchChange("")}
            />
          ) : (
            <Search className="h-4 w-4 text-gray-500 pointer-events-none" />
          )}
        </div>
      </div>
      {!shouldHideAddUser && (
        <Button className="bg-black hover:bg-gray-800 text-white text-sm sm:ml-4" onClick={onAddUser}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      )}
    </div>
  );
}

// Status Filter Tabs Component
function StatusFilterTabs({ currentStatus, onStatusChange, userCounts }) {
  const statusOptions = [
    {
      label: "Active",
      value: "ACTIVE",
      icon: UserCheck,
      color: "text-green-600",
    },
    {
      label: "Inactive",
      value: "INACTIVE",
      icon: Clock,
      color: "text-yellow-600",
    },
    {
      label: "Disabled",
      value: "DISABLED",
      icon: UserX,
      color: "text-red-600",
    },
  ];

  return (
    <div className="inline-flex bg-gray-200 rounded-md mb-6">
      {statusOptions.map(option => {
        const Icon = option.icon;
        const isActive = currentStatus === option.value;
        const count = userCounts[option.value] || 0;

        return (
          <button
            key={option.value}
            onClick={() => onStatusChange(option.value)}
            className={`flex items-center justify-center p-2 transition-all duration-200 ${
              isActive ? "bg-black text-white shadow-sm" : "text-black hover:bg-gray-300"
            }`}
            title={`${option.label} (${count})`}
          >
            <Icon className="w-4 h-4" />
            {count > 0 && (
              <span
                className={`ml-1 px-1.5 py-0.5 text-xs font-medium rounded-full ${
                  isActive ? "bg-white text-black" : "bg-gray-100 text-gray-700"
                }`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Main Role Section Component
export default function RoleSectionTable({
  role,
  section,
  onToggleSection,
  onSearchChange,
  onStatusChange,
  onAddUser,
  onEditUserDetails,
  onDeleteUser,
  onRetry,
  userCounts = {},
  isRoleSubscriberHostOperations: _isRoleSubscriberHostOperations = false,
  isHostPropertyManagement = false,
}) {
  const friendlyRoleName = (() => {
    const roleNameLower = (role.name || "").trim().toLowerCase();
    if (
      roleNameLower === "prop management individual subscriber" ||
      roleNameLower === "property management individual subscriber"
    ) {
      // Rename to "Individual customer accounts" for HOST_PROPERTY_MANAGEMENT
      return isHostPropertyManagement ? "Individual customer accounts" : "Individual accounts";
    } else if (roleNameLower === "property management operations") {
      return "Staff accounts";
    } else if (roleNameLower === "property management host") {
      return "Administrator accounts";
    }
    return role.name;
  })();

  // Determine if "Add User" button should be hidden using centralized access control
  const currentUser = localService.getUser();
  const shouldHideAddUser = !FeatureAccess.canAddUserToRole(currentUser, role.code);
  const canEditUsers = FeatureAccess.canEditUsers(currentUser, role.code);
  const canDeleteUsers = FeatureAccess.canDeleteUsers(currentUser, role.code);

  const userCount = section.users?.length || 0;

  return (
    <div className="p-6 mb-4 bg-white rounded-xl border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Role Icon */}
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">
            <UserPlus className="w-6 h-6 text-gray-600" />
          </div>

          {/* Role Info */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{friendlyRoleName}</h2>
            <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
              <span>{userCount} users</span>
              {/* <span>Last active: {lastActive}</span> */}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {section.open && (
            <Button variant="link" className="text-blue-600 hover:text-blue-700">
              {/* View list */}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onToggleSection} className="p-2">
            {section.open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {section.open && (
        <div className="mt-6">
          <SearchHeader
            searchTerm={section.searchTerm || ""}
            onSearchChange={onSearchChange}
            onAddUser={onAddUser}
            shouldHideAddUser={shouldHideAddUser}
          />

          <StatusFilterTabs
            currentStatus={section.currentStatus}
            onStatusChange={onStatusChange}
            userCounts={userCounts}
          />

          {/* Error State */}
          {section.error && (
            <div className="p-4 mb-6 bg-red-50 rounded-lg border border-red-200">
              <p className="text-red-600">{section.error}</p>
              <button
                onClick={onRetry}
                className="px-4 py-2 mt-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Table */}
          {section.loading ? (
            <TableShimmer />
          ) : section.users.length > 0 ? (
            <UserTable
              users={section.users}
              onEditUserDetails={onEditUserDetails}
              onDeleteUser={onDeleteUser}
              searchTerm={section.searchTerm}
              canEditUsers={canEditUsers}
              canDeleteUsers={canDeleteUsers}
            />
          ) : (
            <div className="py-12 text-center">
              <div className="flex flex-col items-center">
                <UserPlus className="w-12 h-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No {section.currentStatus} users</h3>
                <p className="text-gray-600 mb-4">
                  {shouldHideAddUser
                    ? "No users found with this status."
                    : "Get started by adding a new user to this role."}
                </p>
                {!shouldHideAddUser && (
                  <Button onClick={onAddUser} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
